<template>
    <section class="flex flex-col gap-5">
        <Launch360Animation v-if="processing && isSixty" label="Pulling your reflection together…" @done="onAnimationDone" />
        <ReflectionProcessing v-else-if="processing" />
        <div v-else-if="loading" class="scaffold-body-small text-valence-stone-500">Loading…</div>
        <BaseNotification v-else-if="loadError" theme="error" :text="loadError" hide-dismiss />

        <template v-else-if="isGrouped">
            <h1 class="display-serif text-valence-stone-900">{{ title }}</h1>
            <div v-if="closes" class="scaffold-details-small text-valence-stone-500 -mt-3">{{ closes }}</div>
            <AssessmentGroupedSections
                ref="groupedSections"
                :response-id="responseId"
                :questions="questions"
                :sections="sections"
                :allow-score-examples="allowScoreExamples"
                :allow-not-observed="allowNotObserved"
                :accent-color="accentColor"
                @submitted="onGroupedSubmitted"
                @exit="emit('back')" />
        </template>

        <template v-else-if="currentQuestion">
            <header class="flex flex-col gap-3">
                <div class="flex items-center gap-3 flex-wrap">
                    <h2 class="title-subsection text-valence-stone-900">{{ title }}</h2>
                    <BaseChip v-if="step" size="2xs">{{ step }}</BaseChip>
                </div>
                <div class="flex flex-col gap-2">
                    <BaseProgressBar :value="progressPct" size="sm" :aria-label="`Progress: ${progressPct}%`" :color="accentColor" />
                    <div class="scaffold-details-small text-valence-stone-500">
                        {{ headerStatus }}<template v-if="closes"> · {{ closes }}</template>
                    </div>
                </div>
            </header>

            <QuestionCard
                :question="currentQuestion"
                :score="currentSavedScore"
                :text="currentTextDraft"
                :not-observed="currentSavedNotObserved"
                :allow-score-examples="allowScoreExamples"
                :allow-not-observed="allowNotObserved"
                :show-unanswered="showValidation && isCurrentUnanswered"
                :accent-color="accentColor"
                :accent-surface-color="accentSurfaceColor"
                @update:score="setScore"
                @update:text="currentTextDraft = $event"
                @update:not-observed="setNotObserved"
                @update:busy="composerBusy = $event" />

            <BaseNotification v-if="error" theme="warning" :text="error" hide-dismiss />

            <div class="flex items-center justify-between">
                <button
                    type="button"
                    :disabled="currentIndex === 0 || saving || composerBusy"
                    class="scaffold-body-small text-valence-stone-500 hover:text-valence-stone-900 disabled:opacity-30 flex items-center gap-1"
                    @click="onBack">
                    <i class="bi bi-chevron-left" aria-hidden="true"></i> Back
                </button>

                <BaseButton v-if="!isLast" theme="primary" class="base-btn-dark" :disabled="saving || composerBusy" @click="goNext"> Next </BaseButton>
                <BaseButton v-else theme="primary" class="base-btn-dark" :disabled="submitting || saving || composerBusy" @click="submit">
                    {{ submitting ? "Submitting…" : "Submit" }}
                </BaseButton>
            </div>
            <div v-if="composerBusy" class="scaffold-details-small text-valence-stone-500 text-right -mt-3">Stop recording to continue.</div>
        </template>

        <AssessmentDemoTools v-if="responseId" :response-actions="[{ id: responseId, label: title }]" @filled="onFilled" />

        <SubmitConfirmDialog v-model:open="showSubmitConfirm" title="Submit your responses?" :confirm-disabled="submitting" @confirm="confirmSubmit">
            <p class="body text-valence-stone-500">
                You'll see your results right after. You can come back and update your answers
                <template v-if="closesDate">until this closes on {{ closesDate }}</template>
                <template v-else>anytime while the cycle is open</template>.
            </p>
        </SubmitConfirmDialog>
    </section>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { AssessmentDisplayMode, QuestionResponseData, QuestionWithSaved, ResponseWithQuestions, SectionSummary } from "~vue/api/generated/reflections";
import AssessmentGroupedSections from "~vue/components/AssessmentGroupedSections.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseChip from "~vue/components/BaseChip.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseProgressBar from "~vue/components/BaseProgressBar.vue";
import QuestionCard from "~vue/components/QuestionCard.vue";
import AssessmentDemoTools from "~vue/pages/reflections/AssessmentDemoTools.vue";
import Launch360Animation from "~vue/pages/reflections/Launch360Animation.vue";
import ReflectionProcessing from "~vue/pages/reflections/ReflectionProcessing.vue";
import SubmitConfirmDialog from "~vue/pages/reflections/SubmitConfirmDialog.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { isRequiredUnanswered } from "~vue/utils/assessmentValidation";
import { logUserInteraction } from "~vue/utils/logUtils";
import { closesLabel, formatDate } from "~vue/utils/threeSixty";
import { computed, onMounted, ref, useTemplateRef } from "vue";

interface Props {
    cycleId: string;
    title?: string;
    accentColor?: string;
    accentSurfaceColor?: string;
    // Set when the taker is the last leg of the 360 setup flow, so the header
    // can carry the same step marker as the setup and nomination screens.
    step?: string;
    // The self leg of a 360 keeps the cycle's identity through submission, so it
    // resolves into the pink 360 mark rather than the self-reflection bowl.
    isSixty?: boolean;
    // The cycle's deadline, so the subject knows how long they have to finish.
    // Null for a DRAFT cycle that hasn't been launched yet.
    closesAt?: string | null;
}

const {
    cycleId,
    title = "Self Reflection",
    accentColor = "#FBB877",
    accentSurfaceColor = "var(--color-valence-peach-100)",
    step,
    isSixty = false,
    closesAt = null,
} = defineProps<Props>();

const emit = defineEmits<{
    (event: "submitted"): void;
    (event: "back"): void;
}>();

const questions = ref<QuestionWithSaved[]>([]);
const sections = ref<SectionSummary[]>([]);
const responseId = ref<string>("");
const allowScoreExamples = ref(true);
const allowNotObserved = ref(true);
const displayMode = ref<AssessmentDisplayMode>("one_per_page");
const isGrouped = computed(() => displayMode.value === "grouped_by_section");
const currentIndex = ref(0);
const currentTextDraft = ref<string>("");
const groupedSections = useTemplateRef<InstanceType<typeof AssessmentGroupedSections>>("groupedSections");
const loading = ref(true);
const loadError = ref<string | null>(null);
const error = ref<string | null>(null);
const submitting = ref(false);
const saving = ref(false);
const showSubmitConfirm = ref(false);
const showValidation = ref(false);
// The processing animation plays after a successful submit for at least
// PROCESSING_MIN_MS so the transition feels grounded even when the API returns
// instantly. If the API takes longer, we hold the animation until it resolves —
// no clipped illustration on slow networks. The 360 mark is a one-shot sequence,
// so it reports its own end rather than being timed.
const processing = ref(false);
const PROCESSING_MIN_MS = 2400;
const composerBusy = ref(false);
let animationDone = Promise.resolve();
let resolveAnimation: (() => void) | null = null;

function onAnimationDone() {
    resolveAnimation?.();
}

function startProcessing() {
    if (isSixty) {
        animationDone = new Promise((resolve) => (resolveAnimation = resolve));
    }
    processing.value = true;
}

async function waitForProcessing(startedAt: number) {
    if (isSixty) {
        await animationDone;
        return;
    }
    const remaining = PROCESSING_MIN_MS - (Date.now() - startedAt);
    if (remaining > 0) {
        await new Promise((resolve) => setTimeout(resolve, remaining));
    }
}

function answeredCount(): number {
    return questions.value.filter((q) => q.saved.score !== null || q.saved.not_observed || (q.saved.text ?? "").trim().length > 0).length;
}

function logSubmitted() {
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_SELF_ASSESS_SUBMITTED, {
        cycle_id: cycleId,
        total_questions: questions.value.length,
        answered_count: answeredCount(),
    });
}

onMounted(async () => {
    const { data, error: fetchError, response } = await useFetch(`/v1/reflections/cycles/${cycleId}/self-response`).post().json();
    loading.value = false;
    if (fetchError.value) {
        if (response.value?.status === 410) {
            loadError.value = "This assessment is closed.";
        } else {
            loadError.value = (data.value as { error?: string })?.error || "Could not load self-assessment.";
        }
        return;
    }
    const payload = (data.value as { response: ResponseWithQuestions }).response;
    questions.value = payload.questions;
    sections.value = payload.sections ?? [];
    responseId.value = payload.id;
    allowScoreExamples.value = payload.allow_score_examples ?? true;
    allowNotObserved.value = payload.allow_not_observed ?? true;
    displayMode.value = payload.display_mode ?? "one_per_page";
    currentTextDraft.value = payload.questions[0]?.saved.text ?? "";
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_SELF_ASSESS_VIEWED, {
        cycle_id: cycleId,
        total_questions: payload.questions.length,
        answered_count: answeredCount(),
    });
});

const currentQuestion = computed(() => questions.value[currentIndex.value]);
const currentSavedScore = computed(() => currentQuestion.value?.saved.score ?? null);
const currentSavedNotObserved = computed(() => currentQuestion.value?.saved.not_observed ?? false);
const isLast = computed(() => currentIndex.value === questions.value.length - 1);
const progressPct = computed(() => Math.round(((currentIndex.value + 1) / questions.value.length) * 100));

const headerStatus = computed(() => `Question ${currentIndex.value + 1} of ${questions.value.length}`);

const closes = computed(() => closesLabel(closesAt));
const closesDate = computed(() => formatDate(closesAt, "long"));

const isCurrentUnanswered = computed(() => {
    if (!currentQuestion.value) {
        return false;
    }
    return isRequiredUnanswered(currentQuestion.value);
});

function onFilled(filled: QuestionResponseData[]) {
    const byQuestionId = new Map(filled.map((answer) => [answer.question_id, answer]));
    for (const question of questions.value) {
        const answer = byQuestionId.get(question.id);
        if (answer) {
            question.saved = { score: answer.score ?? null, text: answer.text, not_observed: answer.not_observed };
        }
    }
    groupedSections.value?.adoptSavedAnswers([...byQuestionId.keys()]);
    // Land on the last step so the filled assessment is one click from Submit.
    groupedSections.value?.goToLastPage();
    currentIndex.value = Math.max(questions.value.length - 1, 0);
    currentTextDraft.value = currentQuestion.value?.saved.text ?? "";
    showValidation.value = false;
    error.value = null;
}

function setScore(n: number) {
    if (!currentQuestion.value) {
        return;
    }
    currentQuestion.value.saved = { ...currentQuestion.value.saved, score: n, not_observed: false };
}

function setNotObserved(v: boolean) {
    if (!currentQuestion.value) {
        return;
    }
    const score = v ? null : currentQuestion.value.saved.score;
    currentQuestion.value.saved = { ...currentQuestion.value.saved, score, not_observed: v };
}

async function saveCurrent(): Promise<boolean> {
    if (!currentQuestion.value) {
        return true;
    }
    if (currentTextDraft.value !== currentQuestion.value.saved.text) {
        currentQuestion.value.saved = { ...currentQuestion.value.saved, text: currentTextDraft.value };
    }
    saving.value = true;
    error.value = null;
    try {
        const { error: fetchError } = await useFetch(`/v1/reflections/responses/${responseId.value}/answers`)
            .post(
                JSON.stringify({
                    question_id: currentQuestion.value.id,
                    score: currentQuestion.value.saved.score,
                    text: currentQuestion.value.saved.text,
                    not_observed: currentQuestion.value.saved.not_observed,
                }),
                "application/json",
            )
            .json();
        if (fetchError.value) {
            error.value = "Could not save your answer. Please try again.";
            return false;
        }
        return true;
    } finally {
        saving.value = false;
    }
}

function validateCurrentQuestion(action: "continuing" | "submitting"): boolean {
    if (currentQuestion.value && currentTextDraft.value !== currentQuestion.value.saved.text) {
        currentQuestion.value.saved = { ...currentQuestion.value.saved, text: currentTextDraft.value };
    }
    if (currentQuestion.value && isRequiredUnanswered(currentQuestion.value)) {
        showValidation.value = true;
        error.value = `Please answer this required question before ${action}.`;
        return false;
    }
    showValidation.value = false;
    error.value = null;
    return true;
}

async function goNext() {
    if (currentIndex.value >= questions.value.length - 1) {
        return;
    }
    if (!validateCurrentQuestion("continuing")) {
        return;
    }
    if (!(await saveCurrent())) {
        return;
    }
    currentIndex.value += 1;
    currentTextDraft.value = currentQuestion.value?.saved.text ?? "";
}

async function onBack() {
    // On the first question, "Back" exits the take flow entirely (returns to
    // the hub's list view). Later questions step backward through the flow.
    if (currentIndex.value === 0) {
        emit("back");
        return;
    }
    if (!(await saveCurrent())) {
        return;
    }
    currentIndex.value -= 1;
    currentTextDraft.value = currentQuestion.value?.saved.text ?? "";
}

async function submit() {
    if (submitting.value) {
        return;
    }
    if (!validateCurrentQuestion("submitting")) {
        return;
    }
    if (!(await saveCurrent())) {
        return;
    }
    showSubmitConfirm.value = true;
}

async function confirmSubmit() {
    if (submitting.value) {
        return;
    }
    submitting.value = true;
    // Hide the confirm dialog and swap the questions view for the processing
    // animation as soon as the request goes out — that way the user always
    // sees the transition, not a flash of the taker UI.
    showSubmitConfirm.value = false;
    startProcessing();
    const started = Date.now();
    try {
        const { data, error: fetchError } = await useFetch(`/v1/reflections/responses/${responseId.value}/submit`).post().json();
        if (fetchError.value) {
            error.value = (data.value as { error?: string })?.error || "Could not submit.";
            processing.value = false;
            return;
        }
        logSubmitted();
        await waitForProcessing(started);
        emit("submitted");
    } finally {
        submitting.value = false;
    }
}

// The grouped renderer owns its own save/submit, so the processing animation
// runs for its full duration after the response has already been submitted.
async function onGroupedSubmitted() {
    logSubmitted();
    startProcessing();
    await waitForProcessing(Date.now());
    emit("submitted");
}
</script>
